/**
 * UI preferences — device-local (persisted to localStorage, NOT clinic
 * settings): a receptionist's dense worklist on a big desktop monitor and a
 * doctor's comfortable phone view are different devices, not different
 * clinics.
 *
 * `density` drives a `data-density` attribute on <html>; index.css maps it
 * to spacing CSS variables (--row-py, --card-p) consumed by the busiest
 * list surfaces (appointments table, patients list, check-in rows, lab
 * worklist). Compact ≈ 30% more rows per screen.
 */
import { create } from "zustand";
import { createJSONStorage, persist } from "zustand/middleware";

export type Density = "comfortable" | "compact";

interface UiState {
	density: Density;
	setDensity: (d: Density) => void;
}

function applyDensity(d: Density): void {
	if (typeof document === "undefined") return;
	if (d === "compact") {
		document.documentElement.setAttribute("data-density", "compact");
	} else {
		document.documentElement.removeAttribute("data-density");
	}
}

export const useUiStore = create<UiState>()(
	persist(
		(set) => ({
			density: "comfortable",
			setDensity: (d) => {
				applyDensity(d);
				set({ density: d });
			},
		}),
		{
			name: "klyra.ui",
			storage: createJSONStorage(() => localStorage),
			partialize: (state) => ({ density: state.density }),
			// Re-apply on rehydrate so a reload keeps the compact attribute
			// without waiting for any component to mount.
			onRehydrateStorage: () => (state) => {
				if (state) applyDensity(state.density);
			},
		},
	),
);
